import React, { useState, useRef } from 'react';
import {
	Alert,
	Spin,
	Button,
	Typography,
	Space,
	Modal,
	Row,
	Col,
	Checkbox,
	Divider,
	message,
} from 'antd';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ExclamationCircleFilled,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import PrivacyPolicyViewer from 'components/shared-components/PrivacyPolicyViewer';
import {
	useAcceptConsentMutation,
	useRejectConsentMutation,
} from 'services/ConsentimientosDatosPersonalesApi';
import useCurrentUser from 'utils/hooks/useCurrentUser';
import useAppInitialization from 'utils/hooks/useAppInitialization';

// Configurar dayjs en español
dayjs.locale('es');

const { Text, Paragraph } = Typography;

export const ConsentForm = () => {
	const [user] = useCurrentUser();
	const { forceRefresh, clearUserSession } = useAppInitialization();
	const [accepted, setAccepted] = useState(false);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [hasReaded, setHasReaded] = useState(false);
	const [requiresScroll, setRequiresScroll] = useState(false);
	const [plantillaData, setPlantillaData] = useState(null);

	// Referencia al componente PrivacyPolicyViewer
	const privacyPolicyRef = useRef();

	// Callback cuando se cargan los datos de la plantilla
	const handleDataLoaded = data => {
		setPlantillaData(data);
	};

	// Callback cuando cambia el estado de lectura
	const handleReadStatusChange = ({
		hasReaded: readStatus,
		requiresScroll: scrollRequired,
	}) => {
		setHasReaded(readStatus);
		setRequiresScroll(scrollRequired);
	};

	const [acceptConsent, { isLoading: isAccepting }] =
		useAcceptConsentMutation();
	const [rejectConsent, { isLoading: isRejecting }] =
		useRejectConsentMutation();

	const handleAccept = async () => {
		if (!hasReaded) {
			Modal.warning({
				title: 'Lectura requerida',
				content:
					'Debe leer completamente la Política de Privacidad antes de continuar.',
				centered: true,
			});
			return;
		}

		if (!accepted) {
			Modal.warning({
				title: 'Aceptación requerida',
				content: 'Debe marcar la casilla de aceptación antes de continuar.',
				centered: true,
			});
			return;
		}

		setIsProcessing(true);

		try {
			await acceptConsent({
				plantillaId: plantillaData.id,
				usuarioId: user.id,
				metadata: {
					user_action: 'accept',
					component: 'ConsentForm',
					timestamp: new Date().toISOString(),
					read_tracking: {
						has_readed: hasReaded,
						requires_scroll: requiresScroll,
					},
				},
			}).unwrap();

			message.success('Política de Privacidad aceptada correctamente');

			// Esperar a que forceRefresh complete antes de continuar
			await forceRefresh();
		} catch (error) {
			console.error('Error accepting consent:', error);
			message.error('Error al aceptar la política de privacidad');
		} finally {
			setIsProcessing(false);
		}
	};

	const handleReject = () => {
		setShowRejectModal(true);
	};

	const handleFinalReject = async () => {
		setIsProcessing(true);

		try {
			await rejectConsent({
				plantillaId: plantillaData.id,
				usuarioId: user.id,
				razon_rechazo:
					'Usuario rechazó la Política de Privacidad después de confirmación',
				metadata: {
					user_action: 'reject',
					component: 'ConsentForm',
					confirmation_step: 'single',
					timestamp: new Date().toISOString(),
				},
			}).unwrap();

			message.success('Política de Privacidad rechazada correctamente');

			clearUserSession();
		} catch (error) {
			console.error('Error rejecting consent:', error);
			message.error('Error al rechazar la política de privacidad');
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<>
			<div style={{ position: 'relative' }}>
				{isProcessing && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(255, 255, 255, 0.8)',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							zIndex: 1000,
							borderRadius: '8px',
						}}
					>
						<Spin size='large' />
						<Text
							style={{
								marginTop: '1rem',
								fontSize: '14px',
								color: '#666',
							}}
						>
							Actualizando información...
						</Text>
					</div>
				)}
				<div>
					<PrivacyPolicyViewer
						ref={privacyPolicyRef}
						onDataLoaded={handleDataLoaded}
						onReadStatusChange={handleReadStatusChange}
					/>
					<ActionsWrapper>
						<ConsentCheckbox>
							{!hasReaded && requiresScroll && (
								<Alert
									message='Para continuar, debe leer completamente la Política de Privacidad hasta el final'
									type='info'
									showIcon
									style={{ marginBottom: '1rem', fontSize: '12px' }}
								/>
							)}
							<Checkbox
								checked={accepted}
								onChange={e => setAccepted(e.target.checked)}
								disabled={!hasReaded}
								style={{
									fontSize: '16px',
									opacity: !hasReaded ? 0.6 : 1,
								}}
							>
								<Text
									style={{
										marginLeft: '8px',
										fontSize: '14px',
										color: !hasReaded ? '#bfbfbf' : 'inherit',
									}}
								>
									He leído y acepto la Política de Privacidad. Entiendo
									que al aceptar, autorizo el tratamiento de mis datos
									personales según lo establecido en este documento.
								</Text>
							</Checkbox>
						</ConsentCheckbox>

						<Row gutter={[16, 16]} justify='center'>
							<Col xs={24} sm={12} md={8}>
								<Button
									type='primary'
									size='large'
									icon={<CheckCircleOutlined />}
									onClick={handleAccept}
									loading={isAccepting || isProcessing}
									disabled={!accepted || !hasReaded || isProcessing}
									block
									style={{
										height: '48px',
										fontSize: '16px',
										fontWeight: '500',
									}}
								>
									{isProcessing ? 'Procesando...' : 'Aceptar'}
								</Button>
							</Col>
							<Col xs={24} sm={12} md={8}>
								<Button
									danger
									size='large'
									icon={<CloseCircleOutlined />}
									onClick={handleReject}
									loading={isRejecting}
									block
									style={{
										height: '48px',
										fontSize: '16px',
										fontWeight: '500',
									}}
								>
									No Aceptar
								</Button>
							</Col>
						</Row>

						<Divider style={{ margin: '1.5rem 0 0.5rem 0' }} />

						<Text
							type='primary'
							style={{
								display: 'block',
								textAlign: 'center',
								fontSize: '12px',
								lineHeight: '1.4',
							}}
						>
							Al continuar, confirma que ha leído y entendido nuestra
							política de privacidad.
							<br />
							Si tiene alguna pregunta, puede contactarnos en{' '}
							<Text
								color='primary'
								type='primary'
								code
								style={{ fontSize: '12px' }}
							>
								contacto@ortuzarconsulting.cl
							</Text>
						</Text>
					</ActionsWrapper>
				</div>
			</div>

			{/* Modal de Confirmación de Rechazo */}
			<Modal
				title={
					<Space>
						<ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
						<span style={{ color: '#ff4d4f' }}>Confirmar Rechazo</span>
					</Space>
				}
				open={showRejectModal}
				onCancel={() => setShowRejectModal(false)}
				footer={[
					<Button key='cancel' onClick={() => setShowRejectModal(false)}>
						Cancelar
					</Button>,
					<Button
						key='confirm'
						type='primary'
						danger
						onClick={handleFinalReject}
						loading={isRejecting}
					>
						Confirmar Rechazo
					</Button>,
				]}
				centered
				width={550}
			>
				<div style={{ padding: '1rem 0' }}>
					<Alert
						message={
							<Text style={{ fontSize: '16px', color: '#a8071a' }}>
								<strong>⚠️ ACCIÓN IRREVERSIBLE</strong>
							</Text>
						}
						description={
							<div style={{ marginTop: '1rem' }}>
								<Paragraph
									style={{ fontSize: '14px', marginBottom: '1rem' }}
								>
									<Text strong>
										Al rechazar la Política de Privacidad, su cuenta
										será deshabilitada y se iniciará el proceso de
										depuración completa de todos sus datos.
									</Text>
								</Paragraph>

								<div
									style={{
										background: '#fff2f0',
										padding: '1rem',
										borderRadius: '6px',
										border: '1px solid #ffccc7',
									}}
								>
									<Text style={{ fontSize: '13px', color: '#a8071a' }}>
										<strong>Esto significa que:</strong>
									</Text>
									<ul
										style={{
											marginTop: '0.5rem',
											marginBottom: 0,
											paddingLeft: '1.2rem',
										}}
									>
										<li
											style={{
												fontSize: '12px',
												color: '#a8071a',
												marginBottom: '0.25rem',
											}}
										>
											Su cuenta será desactivada inmediatamente
										</li>
										<li
											style={{
												fontSize: '12px',
												color: '#a8071a',
												marginBottom: '0.25rem',
											}}
										>
											Todos sus datos personales serán anonimizados
											(no eliminados)
										</li>
										<li
											style={{
												fontSize: '12px',
												color: '#a8071a',
												marginBottom: '0.25rem',
											}}
										>
											Los datos no personales permanecerán en el
											sistema para fines estadísticos y de
											trazabilidad
										</li>
										<li
											style={{
												fontSize: '12px',
												color: '#a8071a',
												marginBottom: '0.25rem',
											}}
										>
											No podrá recuperar su información
											posteriormente
										</li>
										<li
											style={{
												fontSize: '12px',
												color: '#a8071a',
												marginBottom: 0,
											}}
										>
											Deberá crear una nueva cuenta para usar
											nuestros servicios
										</li>
									</ul>
								</div>

								<Paragraph
									style={{
										fontSize: '13px',
										marginTop: '1rem',
										marginBottom: 0,
										textAlign: 'center',
									}}
								>
									<Text strong style={{ color: '#ff4d4f' }}>
										¿Está completamente seguro de que desea proceder?
									</Text>
								</Paragraph>
							</div>
						}
						type='error'
						showIcon={false}
						style={{ border: 'none' }}
					/>
				</div>
			</Modal>
		</>
	);
};

// Styled components
const ActionsWrapper = styled.div`
	padding: 1.5rem;
	background: #fff;
	border-top: 1px solid #e6ebf1;
`;

const ConsentCheckbox = styled.div`
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: #f0f7ff;
	border: 1px solid #d4e9ff;
	border-radius: 8px;
`;
